import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Search, ArrowLeft, Package, ShoppingBag, Camera, Clock, Bot, Pencil, Check, X, MapPin, List, Plus } from 'lucide-react';
import { api } from '../../../services/api';
import { DataTable, type SortConfig } from '../tables';
import { Breadcrumb } from './Breadcrumb';
import { HealthScoreCircle } from './HealthBadge';
import { SlugVerificationBadge, type VerificationStatus } from './SlugVerificationBadge';
import { useLanguage } from '../../../contexts/LanguageContext';

interface StoreListItem {
  id: string;
  name: string;
  slug: string | null;
  slug_verification_status: VerificationStatus;
  slug_verified_at: string | null;
  city: string | null;
  state: string | null;
  address_verification_status: string | null;
  as_is_url: string | null;
  product_count: number;
  last_chance_count: number;
  family_offers_count: number;
  limited_time_count: number;
  physical_count: number;
  last_seed_run: {
    date: string | null;
    status: string | null;
    products_found: number | null;
  };
  health: {
    score: number;
    status: 'healthy' | 'warning' | 'error';
    freshness_24h: number;
    freshness_7d: number;
    completeness: number;
    stale_count: number;
  };
  assigned_seed_id: number | null;
  assigned_seed_name: string | null;
}

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

// Inline editable slug component
function SlugEditor({
  storeId,
  currentSlug,
  verificationStatus,
  verifiedAt,
  onSave
}: {
  storeId: string;
  currentSlug: string | null;
  verificationStatus?: VerificationStatus;
  verifiedAt?: string | null;
  onSave: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(currentSlug || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState<VerificationStatus | undefined>(verificationStatus);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local status with prop when it changes
  useEffect(() => {
    setLocalStatus(verificationStatus);
  }, [verificationStatus]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    const newSlug = value.trim().toLowerCase() || null;

    // Validate format - allow letters (including unicode), numbers, hyphens, plus signs
    if (newSlug && /\s/.test(newSlug)) {
      setError('Slugs cannot contain spaces');
      return;
    }

    // Skip if unchanged
    if (newSlug === currentSlug) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await api.updateStoreSlug(storeId, newSlug);
      // Update local status immediately from API response
      if (response.store?.slug_verification_status) {
        setLocalStatus(response.store.slug_verification_status as VerificationStatus);
      }
      setIsEditing(false);
      onSave(); // Trigger refresh for other data
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(currentSlug || '');
    setError(null);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div
        className="flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="group"
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="store-slug"
          disabled={saving}
          className={`w-24 px-1.5 py-0.5 text-xs font-mono border rounded ${
            error ? 'border-red-400 bg-red-50' : 'border-gray-300'
          } focus:outline-none focus:ring-1 focus:ring-primary-500`}
        />
        <button
          onClick={handleSave}
          disabled={saving}
          className="p-0.5 text-green-600 hover:bg-green-50 rounded"
          title="Save"
        >
          <Check size={12} />
        </button>
        <button
          onClick={handleCancel}
          disabled={saving}
          className="p-0.5 text-gray-400 hover:bg-gray-100 rounded"
          title="Cancel"
        >
          <X size={12} />
        </button>
        {error && (
          <span className="text-xs text-red-500 ml-1">{error}</span>
        )}
      </div>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs text-gray-400 font-mono cursor-pointer hover:text-gray-600 group bg-transparent border-none p-0"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        title="Click to edit slug"
      >
        slug: {currentSlug || <span className="italic text-gray-300">none</span>}
        <Pencil size={10} className="opacity-0 group-hover:opacity-100" />
      </button>
      {currentSlug && localStatus && (
        <SlugVerificationBadge
          status={localStatus}
          verifiedAt={verifiedAt}
          showLabel={false}
          size="sm"
        />
      )}
    </span>
  );
}

export function StoreListView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { country } = useParams<{ country: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const listRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  // Get page from URL, default to 1
  const page = parseInt(searchParams.get('page') || '1', 10);
  const viewAll = searchParams.get('viewAll') === 'true';

  // Create store modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    city: '',
    state: '',
    street_address: '',
    postal_code: '',
    slug: '',
  });
  const [createError, setCreateError] = useState<string | null>(null);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(() => {
    const sortKey = searchParams.get('sort');
    const sortDir = searchParams.get('sortDir') as 'asc' | 'desc' | null;
    return sortKey ? { key: sortKey, direction: sortDir || 'asc' } : null;
  });

  // Update URL when page changes
  const setPage = useCallback((newPage: number) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('page', newPage.toString());
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // Scroll to top when page changes (after data loads)
  const prevPageRef = useRef(page);
  useEffect(() => {
    if (prevPageRef.current !== page) {
      prevPageRef.current = page;
      listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [page]);

  // Toggle view all
  const toggleViewAll = useCallback(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (viewAll) {
        params.delete('viewAll');
      } else {
        params.set('viewAll', 'true');
        params.set('page', '1');
      }
      return params;
    }, { replace: true });
  }, [viewAll, setSearchParams]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-country-stores', country, page, search, sortConfig?.key, sortConfig?.direction, viewAll],
    queryFn: () =>
      api.getAdminCountryStores(country!, {
        page,
        limit: viewAll ? 1000 : 25,
        search: search || undefined,
        sort: sortConfig?.key,
        sortDirection: sortConfig?.direction,
      }),
    enabled: !!country,
  });

  // Create store mutation
  const createStoreMutation = useMutation({
    mutationFn: (storeData: typeof createForm & { country: string }) => api.createStore(storeData),
    onSuccess: (response) => {
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        city: '',
        state: '',
        street_address: '',
        postal_code: '',
        slug: '',
      });
      setCreateError(null);
      queryClient.invalidateQueries({ queryKey: ['admin-country-stores', country] });
      // Navigate to the new store's detail page
      if (response.store?.id) {
        navigate(`/admin/store-browser/${country}/${response.store.id}`);
      }
    },
    onError: (err: Error) => {
      setCreateError(err.message || 'Failed to create store');
    },
  });

  const handleCreateStore = () => {
    if (!createForm.name.trim()) {
      setCreateError('Store name is required');
      return;
    }
    setCreateError(null);
    createStoreMutation.mutate({
      ...createForm,
      country: country!,
    });
  };

  const handleSort = (key: string) => {
    setSortConfig((current): SortConfig => {
      const newDirection = current?.key === key && current.direction === 'asc' ? 'desc' : 'asc';
      const newConfig: SortConfig = { key, direction: newDirection };

      // Persist to URL
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set('sort', newConfig.key);
        params.set('sortDir', newConfig.direction);
        params.set('page', '1');
        return params;
      }, { replace: true });

      return newConfig;
    });
  };

  // Update search in URL with debounce effect (only when search actually changes)
  const prevSearchRef = useRef(search);
  useEffect(() => {
    // Skip if search hasn't changed (prevents resetting page on mount/navigation)
    if (prevSearchRef.current === search) return;

    const timeoutId = setTimeout(() => {
      prevSearchRef.current = search;
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        if (search) {
          params.set('search', search);
        } else {
          params.delete('search');
        }
        params.set('page', '1'); // Only reset page when search changes
        return params;
      }, { replace: true });
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [search, setSearchParams]);

  const columns = [
    {
      key: 'name',
      header: 'Store',
      sortable: true,
      render: (store: StoreListItem) => (
        <div className="flex flex-col gap-1">
          <span className="font-medium text-gray-900">{store.name}</span>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <MapPin
              size={10}
              className={store.address_verification_status === 'verified' ? 'text-green-500' : 'text-gray-400'}
            />
            <span>
              {store.city && store.state ? `${store.city}, ${store.state}` : store.city || store.state || 'No address'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
            <span>ID: {store.id}</span>
            <span>•</span>
            <SlugEditor
              storeId={store.id}
              currentSlug={store.slug}
              verificationStatus={store.slug_verification_status}
              verifiedAt={store.slug_verified_at}
              onSave={() => refetch()}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'health',
      header: 'Health',
      sortable: true,
      sortKey: 'health_score',
      render: (store: StoreListItem) => (
        <HealthScoreCircle score={store.health.score} size={36} strokeWidth={3} />
      ),
    },
    {
      key: 'products',
      header: 'Data Sources',
      render: (store: StoreListItem) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-xs">
            <Package size={12} className="text-blue-500" />
            <span className="text-gray-700">{(store.product_count || 0).toLocaleString()}</span>
            <span className="text-gray-400">As-Is</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <ShoppingBag size={12} className="text-orange-500" />
            <span className="text-gray-700">{(store.last_chance_count || 0).toLocaleString()}</span>
            <span className="text-gray-400">Last Chance</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <Camera size={12} className="text-purple-500" />
            <span className="text-gray-700">{(store.physical_count || 0).toLocaleString()}</span>
            <span className="text-gray-400">Physical</span>
          </div>
        </div>
      ),
    },
    {
      key: 'freshness',
      header: 'Freshness',
      sortable: true,
      sortKey: 'freshness_24h',
      render: (store: StoreListItem) => (
        <div className="flex flex-col gap-1">
          <div className="text-xs">
            <span className="text-gray-500">24h:</span>{' '}
            <span className={`font-medium ${store.health.freshness_24h >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
              {store.health.freshness_24h}%
            </span>
          </div>
          <div className="text-xs">
            <span className="text-gray-500">7d:</span>{' '}
            <span className={`font-medium ${store.health.freshness_7d >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
              {store.health.freshness_7d}%
            </span>
          </div>
          {store.health.stale_count > 0 && (
            <div className="text-xs text-red-500">
              {store.health.stale_count} stale
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'last_run',
      header: 'Last Seed Run',
      sortable: true,
      sortKey: 'last_run_at',
      render: (store: StoreListItem) => {
        if (!store.last_seed_run.date) {
          return <span className="text-gray-400 text-sm">Never</span>;
        }

        const statusColor =
          store.last_seed_run.status === 'success'
            ? 'text-green-600'
            : store.last_seed_run.status === 'error'
            ? 'text-red-600'
            : 'text-yellow-600';

        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <Clock size={12} className="text-gray-400" />
              <span className="text-sm text-gray-600">
                {formatTimeAgo(store.last_seed_run.date)}
              </span>
            </div>
            <span className={`text-xs font-medium ${statusColor}`}>
              {store.last_seed_run.status}
              {store.last_seed_run.products_found !== null && (
                <span className="text-gray-500 font-normal">
                  {' '}
                  ({store.last_seed_run.products_found} found)
                </span>
              )}
            </span>
          </div>
        );
      },
    },
    {
      key: 'seed',
      header: 'Assigned Seed',
      render: (store: StoreListItem) => {
        if (!store.assigned_seed_name) {
          return <span className="text-gray-400 text-sm">Unassigned</span>;
        }
        return (
          <div className="flex items-center gap-1.5">
            <Bot size={14} className="text-primary-500" />
            <span className="text-sm text-gray-700">{store.assigned_seed_name}</span>
          </div>
        );
      },
    },
  ];

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load stores</p>
        <button
          onClick={() => navigate('/admin/store-browser')}
          className="mt-4 text-primary-600 hover:text-primary-700"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div ref={listRef} className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[{ label: data?.country.name || country || '' }]}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/store-browser')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {data?.country.name || country}
            </h1>
            <p className="text-sm text-gray-500">
              {data?.total || 0} stores
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Add Store Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <Plus size={16} />
            {t('storeListView.addStore', 'Add Store')}
          </button>

          {/* View All Toggle */}
          <button
            onClick={toggleViewAll}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              viewAll
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <List size={16} />
            {viewAll ? t('storeListView.paginatedView', 'Paginated View') : t('storeListView.viewAll', 'View All')}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search stores..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <DataTable<StoreListItem>
        columns={columns}
        data={data?.stores || []}
        loading={isLoading}
        page={viewAll ? 1 : page}
        totalPages={viewAll ? 1 : (data?.totalPages || 1)}
        onPageChange={setPage}
        onRowClick={(store) => navigate(`/admin/store-browser/${country}/${store.id}`, { state: { fromPage: page } })}
        sortConfig={sortConfig}
        onSort={handleSort}
        hidePagination={viewAll}
      />

      {/* Create Store Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Add New Store</h2>
              <p className="text-sm text-gray-500 mb-4">
                Adding store to {data?.country.name || country}
              </p>

              <div className="space-y-4">
                {/* Store Name */}
                <div>
                  <label htmlFor="store-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Store Name *
                  </label>
                  <input
                    id="store-name"
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., IKEA Brooklyn"
                  />
                </div>

                {/* City & State */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="store-city" className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      id="store-city"
                      type="text"
                      value={createForm.city}
                      onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Brooklyn"
                    />
                  </div>
                  <div>
                    <label htmlFor="store-state" className="block text-sm font-medium text-gray-700 mb-1">
                      State/Province
                    </label>
                    <input
                      id="store-state"
                      type="text"
                      value={createForm.state}
                      onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="NY"
                    />
                  </div>
                </div>

                {/* Street Address */}
                <div>
                  <label htmlFor="store-street" className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address
                  </label>
                  <input
                    id="store-street"
                    type="text"
                    value={createForm.street_address}
                    onChange={(e) => setCreateForm({ ...createForm, street_address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="1 Beard St"
                  />
                </div>

                {/* Postal Code & Phone */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="store-postal" className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code
                    </label>
                    <input
                      id="store-postal"
                      type="text"
                      value={createForm.postal_code}
                      onChange={(e) => setCreateForm({ ...createForm, postal_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="11231"
                    />
                  </div>

                </div>

                {/* Slug */}
                <div>
                  <label htmlFor="store-slug" className="block text-sm font-medium text-gray-700 mb-1">
                    Slug
                  </label>
                  <input
                    id="store-slug"
                    type="text"
                    value={createForm.slug}
                    onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    placeholder="brooklyn"
                  />
                  <p className="text-xs text-gray-400 mt-1">Used for IKEA website URLs</p>
                </div>

                {createError && (
                  <p className="text-sm text-red-600">{createError}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateError(null);
                    setCreateForm({
                      name: '',
                      city: '',
                      state: '',
                      street_address: '',
                      postal_code: '',
                      slug: '',
                    });
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateStore}
                  disabled={createStoreMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {createStoreMutation.isPending ? 'Creating...' : 'Create Store'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
